<script setup lang="ts">

import { RouterView, useRouter, isNavigationFailure, NavigationFailureType } from 'vue-router';
import { onMounted, onUnmounted, ref } from 'vue';
import ErrorHandler from '@/classes/ErrorHandler';
import { bootTrace } from '@/utility/bootTrace';

/** Oltre questa soglia la prima navigazione è anomala: lo segnaliamo a Sentry. */
const WATCHDOG_MS = 12000;

/** Oltre questa soglia smettiamo di aspettare e diamo all'utente qualcosa su cui agire. */
const FAILSAFE_MS = 20000;

const router = useRouter();

const bootFailed = ref(false);

let watchdogTimer: ReturnType<typeof setTimeout> | undefined;
let failsafeTimer: ReturnType<typeof setTimeout> | undefined;

function clearTimers() {
	clearTimeout(watchdogTimer);
	clearTimeout(failsafeTimer);
}

function retry() {
	window.location.reload();
}

onMounted(() => {
	// Segnale esplicito per il watchdog di WordPress (get_vue_app_html): se l'app è viva
	// è lei a gestire loader ed errori, e il watchdog deve tirarsi indietro. Contare i
	// figli di #app non basta: una route può renderizzare solo commenti finché non è pronta.
	document.documentElement.setAttribute('data-vue-app', 'mounted');

	const mainLoader = document.getElementById('main-loader');
	const hideLoader = () => {
		if (mainLoader) mainLoader.style.display = 'none';
	};

	let settled = false;

	// Il loader è markup di WordPress, fuori dall'app: se non lo nascondiamo noi non lo
	// nasconde nessuno. Prima veniva nascosto solo nel ramo `.then()` di isReady(), quindi
	// qualunque navigazione che non risolveva (chiamata di rete appesa, guard che non
	// chiama next(), navigazione abortita) lasciava l'utente davanti a uno spinner eterno.
	watchdogTimer = setTimeout(() => {
		if (settled) return;
		ErrorHandler.captureBusinessError('vue-app: prima navigazione non completata', {
			url: window.location.href,
			elapsed_ms: WATCHDOG_MS,
			effective_connection: (navigator as any)?.connection?.effectiveType ?? null,
			time_since_navigation_start_ms: Math.round(performance.now()),
		});
	}, WATCHDOG_MS);

	failsafeTimer = setTimeout(() => {
		if (settled) return;
		bootTrace('App.vue: failsafe scattato, mostro errore', {
			currentRoute: router.currentRoute.value.fullPath,
		});
		hideLoader();
		bootFailed.value = true;
	}, FAILSAFE_MS);

	let succeeded = false;

	router
		.isReady()
		.then(() => {
			succeeded = true;
		})
		.catch((error: unknown) => {
			// Una navigazione annullata significa che è già in corso un redirect
			// (es. verso il login di WordPress): non è un errore da mostrare.
			const isAborted =
				isNavigationFailure(error, NavigationFailureType.aborted) ||
				isNavigationFailure(error, NavigationFailureType.cancelled);

			succeeded = isAborted;

			// La cattura su Sentry è già fatta da router.onError: vue-router, in
			// triggerError, notifica i listener e rigetta isReady() con lo stesso errore.
			// Qui ci limitiamo a lasciare traccia del perché il loader viene sbloccato.
			if (!isAborted) {
				ErrorHandler.addLog('router.isReady() rigettata: mostro il fallback', error);
			}
		})
		.finally(() => {
			settled = true;
			clearTimers();
			hideLoader();
			// Se il failsafe era già scattato ma l'app è arrivata comunque, torniamo alla form.
			bootFailed.value = !succeeded;
		});
});

onUnmounted(clearTimers);

</script>

<template>
	<div v-if="bootFailed" class="it-page-section my-5" role="alert">
		<h2 class="h4">Non è stato possibile caricare il servizio</h2>
		<p>
			Il caricamento non è andato a buon fine, probabilmente per un problema temporaneo di
			connessione. Riprova: se il problema persiste, contatta l'assistenza.
		</p>
		<button type="button" class="btn btn-primary" @click="retry">Riprova</button>
	</div>
	<RouterView v-else />
</template>
